import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { TokenStorageService } from './login/token-storage.service';
import { AuthService } from './login/auth.service';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { NetworkErrorService } from './services/network-error.service';
import { NO_REPLAY } from './login/no-replay';
import { KITCHEN_URL, RELOGIN_URL, WAITER_URL } from './login/landing';
/**
 * HTTP interceptor that handles dual-token authentication.
 *
 * Her istekte:
 *   1. access token varsa Authorization header'ı ekler
 *   2. 401 alırsa /auth/refresh ile token'ı yeniler
 *   3. Yenileme başarılıysa orijinal isteği retry eder
 *   4. Yenileme başarısızsa logout + /login yönlendirmesi yapar (garson /
 *      mutfak ekranında o ekranın "Tekrar giriş" sayfasına)
 *
 * Eş zamanlı 401'ler: Refresh devam ederken gelen diğer istekler
 * refreshSubject üzerinden bekler, refresh bitince hepsi devam eder.
 *
 * NOT: AuthService burada Injector üzerinden alınır. Doğrudan constructor
 * inject edilseydi Angular DI döngüsel bağımlılık hatası verirdi:
 * AuthInterceptor → AuthService → HttpClient → HTTP_INTERCEPTORS → AuthInterceptor
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private tokenStorage: TokenStorageService,
    private router: Router,
    private injector: Injector
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Auth endpoint'lerine token ekleme — sonsuz döngü önlemi
    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }

    // Sadece kendi backend URL'imize token ekle
    if (!req.url.startsWith(environment.baseUrl)) {
      return next.handle(req);
    }

    const accessToken = this.tokenStorage.getAccessToken();

    // Token yoksa veya istek zaten Authorization header'ı taşıyorsa dokunma
    // (eski interceptor'daki req.headers.has('Authorization') koruması)
    if (!accessToken || req.headers.has('Authorization')) {
      return next.handle(req).pipe(
        catchError(error => {
          if (this.shouldRefresh(req, error)) {
            return this.handle401(req, next, null);
          }
          this.checkNetworkError(error);
          return throwError(() => error);
        })
      );
    }

    const authedReq = this.addToken(req, accessToken);

    return next.handle(authedReq).pipe(
      catchError(error => {
        if (this.shouldRefresh(req, error)) {
          return this.handle401(req, next, accessToken);
        }
        this.checkNetworkError(error);
        return throwError(() => error);
      })
    );
  }



  /**
   * Herhangi bir API isteğinde status 0 (ağ hatası) gelirse.
   * Status 0 yalnızca offline anlamına gelmez — iptal edilen istekler,
   * büyük payload timeout'ları ve navigasyon sırasında kesilen istekler
   * de status 0 üretir. Bu yüzden önce gerçek ağ durumunu kontrol ediyoruz.
   */
  private checkNetworkError(error: any): void {
    if (error instanceof HttpErrorResponse && error.status === 0 && Capacitor.isNativePlatform()) {
      Network.getStatus().then(networkStatus => {
        if (!networkStatus.connected) {
          console.warn('[AuthInterceptor] Device is offline — showing banner.', error.url);
          const networkService = this.injector.get(NetworkErrorService);
          networkService.showError();
        } else {
          // İptal edilen istek, büyük payload, timeout vs. — banner açma.
          console.warn('[AuthInterceptor] status:0 but device is online (aborted/timeout):', error.url);
        }
      });
    }
  }

  /**
   * 401 alındığında refresh token ile yeni access token alır ve isteği tekrar dener.
   * Eş zamanlı birden fazla 401 olursa sadece bir refresh isteği gönderilir,
   * diğerleri refreshSubject üzerinden sırayla devam eder.
   */
  private handle401(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    sentToken: string | null
  ): Observable<HttpEvent<unknown>> {
    // Token zaten yenilenmiş (başka istek/sekme): yeniden rotate etme, yenisiyle dene.
    const latest = this.tokenStorage.getAccessToken();
    if (sentToken && latest && latest !== sentToken) {
      return next.handle(this.addToken(req, latest));
    }

    if (this.isRefreshing) {
      // Refresh devam ediyor — yeni token gelene kadar bekle
      return this.refreshSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(req, token!)))
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    // Döngüsel DI'dan kaçınmak için AuthService'i lazy inject et
    const authService = this.injector.get(AuthService);

    // NOT: catchError, retry switchMap'ten ÖNCE. Böylece SADECE /auth/refresh
    // hatasını yakalar — retry edilen orijinal isteğin 401'ini DEĞİL. Aksi halde
    // (eski sıralama) yeni access token ile retry edilen istek 401 dönerse
    // logout() tetiklenip Preferences'taki geçerli refresh token silinirdi.
    return authService.refreshAccessToken().pipe(
      catchError((err: any) => {
        this.isRefreshing = false;
        // Başka sekme token'ı bu arada rotate ettiyse oturum ölü değil.
        const rotated = this.tokenStorage.getAccessToken();
        if (rotated && rotated !== sentToken) {
          return of({ accessToken: rotated });
        }
        // Geçici hata (ağ yok / 5xx): refresh token HÂLÂ geçerli — silme.
        // Session'ı koru, hatayı yukarı bırak; bir sonraki istek yeniden dener.
        // Cihaz gerçekten offline ise kullanıcıya banner göster.
        if (this.isTransientError(err)) {
          this.checkNetworkError(err);
          return throwError(() => err);
        }
        // Refresh gerçekten reddedildi (401/403 vb.) → session sona erdi.
        console.warn('[AuthInterceptor] Refresh rejected, logging out:', err?.status, err?.error);
        authService.logout();
        this.goToLogin();
        return throwError(() => err);
      }),
      switchMap(({ accessToken }: { accessToken: string }) => {
        this.isRefreshing = false;
        this.refreshSubject.next(accessToken);
        return next.handle(this.addToken(req, accessToken));
      })
    );
  }

  /**
   * After a rejected refresh: the login page, or — on a waiter / kitchen
   * screen, which nobody may be watching closely — the "Tekrar giriş" page
   * of that screen, so the session end is plain instead of a frozen board.
   */
  private goToLogin(): void {
    const path = (this.router.url ?? '').split(/[?#]/)[0];
    const on = (url: string) => path === url || path.startsWith(`${url}/`);
    const screen = on(WAITER_URL) ? 'waiter' : on(KITCHEN_URL) ? 'kitchen' : null;
    if (screen) this.router.navigate([RELOGIN_URL], { queryParams: { screen } });
    else this.router.navigate(['/login']);
  }

  /**
   * 401 → rotate and resend, except a request that must go exactly once
   * (NO_REPLAY), and never on a page that has handed its session over (C29).
   */
  private shouldRefresh(req: HttpRequest<unknown>, error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401
      && !req.context.get(NO_REPLAY) && !this.tokenStorage.isHandingOver();
  }

  /**
   * Geçici (transient) hata mı? Ağ kopması (status 0) veya sunucu hatası (5xx).
   * Bu durumlarda refresh token geçerliliğini yitirmez — session korunmalı.
   * 401/403 veya HttpErrorResponse olmayan hatalar → oturum ölü kabul edilir.
   */
  private isTransientError(err: any): boolean {
    return err instanceof HttpErrorResponse && (err.status === 0 || err.status === 429 || err.status >= 500);
  }

  private addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * /auth/login, /auth/refresh, /auth/logout, /auth/switch-profile endpoint'lerine
   * token eklenmez — kimlik istek gövdesinden gelir.
   */
  private isAuthEndpoint(url: string): boolean {
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout') ||
      url.includes('/auth/switch-profile')
    );
  }
}
